'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useId, useRef } from 'react';
const OVERLAY_HISTORY_KEY = '__welloOverlay';
const getCurrentMarker = () => typeof window === 'undefined' ? undefined : window.history.state?.[OVERLAY_HISTORY_KEY] as string | undefined;

/** 현재 히스토리 엔트리가 오버레이가 만든 임시 엔트리인지 — 라우트 이동이 이 위를 덮어쓴다. */
export const hasOverlayHistoryEntry = () => getCurrentMarker() !== undefined;

/**
 * 화면을 덮는 오버레이가 브라우저·앱 뒤로가기로 닫히게 한다.
 * 열릴 때 history 엔트리를 하나 push하고 그 엔트리가 pop되면 close를 부른다.
 * X·backdrop·ESC로 닫힌 경우엔 남은 엔트리를 back으로 소비해 히스토리가 쌓이지 않게 한다.
 *
 * ⚠️ **오버레이를 그리는 컴포넌트가 아니라 여는 상태를 소유한 쪽**에서 부른다.
 * 그리는 쪽(DS Modal·BottomDrawer)에 걸면 세 가지가 깨진다 —
 * ① 반응형 분기가 Modal↔Drawer로 갈아탈 때 마커가 두 번 쌓이거나 소실되고
 * ② 컨테이너가 dynamic import로 늦게 마운트해 열린 직후 back이 무방비이며
 * ③ onClose가 optional인 소비처는 pop돼도 닫을 방법이 없다.
 * 그래서 close를 필수 인자로 받는다 — 닫을 수 없는 쪽은 애초에 이 계약을 질 수 없다.
 */
export const useOverlayHistory = (opened, close) => {
  const $ = _c(9);
  const id = useId();
  const hasPushedRef = useRef(false);
  let t0;
  let t1;
  if ($[0] !== close || $[1] !== id || $[2] !== opened) {
    t0 = () => {
      if (!opened) {
        return;
      }
      if (!hasPushedRef.current) {
        hasPushedRef.current = true;
        window.history.pushState({
          ...window.history.state,
          [OVERLAY_HISTORY_KEY]: id
        }, "");
      }
      const handlePopState = () => {
        if (getCurrentMarker() === id) {
          return;
        }
        hasPushedRef.current = false;
        close();
      };
      window.addEventListener("popstate", handlePopState);
      return () => {
        window.removeEventListener("popstate", handlePopState);
      };
    };
    t1 = [opened, close, id];
    $[0] = close;
    $[1] = id;
    $[2] = opened;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[5] !== id || $[6] !== opened) {
    t2 = () => {
      if (opened || !hasPushedRef.current) {
        return;
      }
      hasPushedRef.current = false;
      if (getCurrentMarker() === id) {
        window.history.back();
      }
    };
    t3 = [opened, id];
    $[5] = id;
    $[6] = opened;
    $[7] = t2;
    $[8] = t3;
  } else {
    t2 = $[7];
    t3 = $[8];
  }
  useEffect(t2, t3);
};